import { useEffect, useState } from 'react';
import { Charge, ChargeSchema, update } from '@/api/fee/charge';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateCharge({ charge }: { charge: Charge }) {
	const transSummaryFields: Array<string> = [
		'CPICAssessment',
		'EMPAFee',
		'FarmBureauFee',
		'FHCFAssessment',
		'FilingFee',
		'InspectionFee',
		'PolicyFee',
		'ProviderFee',
		'SLTax',
		'StampingFee',
		'StateFee1',
	];

	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (updatedCharge: Charge) => update(updatedCharge),
		onSuccess: () => {
			toast.success(`Success! Charge has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: ['charges'],
			});
			reset();
		},
		onError: () => {
			toast.error(`Error! Charge could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<Charge>({
		resolver: zodResolver(ChargeSchema),
		mode: 'all',
		defaultValues: charge,
	});

	const { reset } = form;
	useEffect(() => {
		reset(charge);
	}, [charge, reset]);
	
	const onSubmit: SubmitHandler<Charge> = (values: Charge) => mutate(values);

	return (
		<>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update charge</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Update Charge</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="chargeName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Charge Name</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="transSummaryField"
									render={({ field }) => (
										<FormItem>
											<FormLabel>TransSummary Field</FormLabel>
											<FormControl>
												<Select
													value={field.value ?? 'none'}
													onValueChange={(val) =>
														field.onChange(val === 'none' ? null : val)
													}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select a field..." />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectLabel>TransSummary Fields</SelectLabel>
															<SelectItem key="none" value="none"></SelectItem>
															{transSummaryFields!.map((field) => (
																<SelectItem key={field} value={field}>
																	{field}
																</SelectItem>
															))}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="isTax"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
											<div className="space-y-0.5">
												<FormLabel>Tax</FormLabel>
											</div>
											<FormControl>
												<Switch checked={!!field.value} onCheckedChange={field.onChange} />
											</FormControl>
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="isExpired"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
											<div className="space-y-0.5">
												<FormLabel>Expired</FormLabel>
											</div>
											<FormControl>
												<Switch checked={!!field.value} onCheckedChange={field.onChange} />
											</FormControl>
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Updating...' : 'Update'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</>
	);
}
